import { inject, Injectable } from "@angular/core";
import { Cartesian2, Cartesian3, Cartographic, Ellipsoid } from "cesium";
import { CesiumMapService } from "..";

/* NOTE: GeoUtilsService is extracted from angular-cesium with updated types and minor optimization  */

export interface Vec3 {
    x: number;
    y: number;
    z: number;
}

const MAX_ITERATIONS = 16;
const RATIO_TOLERANCE = 1.000001;
const PI_WRAP_OFFSET_MULTIPLIER = 3;

@Injectable({
    providedIn: "root",
})
export class GeoUtilsService {
    private readonly cesiumService: CesiumMapService = inject(CesiumMapService);

    public static pointByLocationDistanceAndAzimuth(
        currentLocation: Cartesian3 | Cartographic,
        meterDistance: number,
        radianAzimuth: number
    ) {
        const distance = meterDistance / Ellipsoid.WGS84.maximumRadius;
        const cartographicLocation = currentLocation instanceof Cartesian3 ? Cartographic.fromCartesian(currentLocation) : currentLocation;
        const cartesianLocation =
            currentLocation instanceof Cartesian3
                ? currentLocation
                : Cartesian3.fromRadians(currentLocation.longitude, currentLocation.latitude, currentLocation.height);

        let resultPosition;
        let resultDistance = Infinity;
        let counter = 0;
        let distanceFactorRangeMax = 0.1;
        let distanceFactorRangeMin = -0.1;

        while (
            counter === 0 ||
            (counter < MAX_ITERATIONS &&
                Math.max(resultDistance, meterDistance) / Math.min(resultDistance, meterDistance) > RATIO_TOLERANCE)
        ) {
            const factor = distanceFactorRangeMin + (distanceFactorRangeMax - distanceFactorRangeMin) / 2;
            resultPosition = GeoUtilsService.innerPointByLocationDistanceAndAzimuth(
                cartographicLocation,
                distance * (1 + factor),
                radianAzimuth
            );
            resultDistance = this.distance(cartesianLocation, resultPosition);

            if (resultDistance > meterDistance) {
                distanceFactorRangeMax = distanceFactorRangeMin + (distanceFactorRangeMax - distanceFactorRangeMin) / 2;
            } else {
                distanceFactorRangeMin = distanceFactorRangeMin + (distanceFactorRangeMax - distanceFactorRangeMin) / 2;
            }
            counter++;
        }

        return resultPosition;
    }

    private static innerPointByLocationDistanceAndAzimuth(cartographicLocation: Cartographic, distance: number, radianAzimuth: number) {
        const { latitude, longitude } = cartographicLocation;
        const destinationLat = Math.asin(
            Math.sin(latitude) * Math.cos(distance) + Math.cos(latitude) * Math.sin(distance) * Math.cos(radianAzimuth)
        );

        let destinationLon =
            longitude +
            Math.atan2(
                Math.sin(radianAzimuth) * Math.sin(distance) * Math.cos(latitude),
                Math.cos(distance) - Math.sin(latitude) * Math.sin(destinationLat)
            );

        destinationLon = ((destinationLon + PI_WRAP_OFFSET_MULTIPLIER * Math.PI) % (2 * Math.PI)) - Math.PI;

        return Cartesian3.fromRadians(destinationLon, destinationLat);
    }

    public static distance(firstPosition: Cartesian3, secondPosition: Cartesian3): number {
        return Cartesian3.distance(firstPosition, secondPosition);
    }

    public static getPositionsDelta(position0: Cartesian3, position1: Cartesian3): Vec3 {
        return {
            x: position1.x - position0.x,
            y: position1.y - position0.y,
            z: position1.z - position0.z,
        };
    }

    public static addDeltaToPosition(position: Cartesian3, delta: Vec3, updateReference = false): Cartesian3 {
        if (updateReference) {
            position.x += delta.x;
            position.y += delta.y;
            position.z += delta.z;
            const cartographic = Cartographic.fromCartesian(position);
            cartographic.height = 0;
            const cartesian = Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude, cartographic.height);
            position.x = cartesian.x;
            position.y = cartesian.y;
            position.z = cartesian.z;

            return position;
        } else {
            const cartesian = new Cartesian3(position.x + delta.x, position.y + delta.y, position.z + delta.z);
            const cartographic = Cartographic.fromCartesian(cartesian);
            cartographic.height = 0;

            return Cartesian3.fromRadians(cartographic.longitude, cartographic.latitude, cartographic.height);
        }
    }

    public static middleCartesian3Point(position0: Cartesian3, position1: Cartesian3) {
        return new Cartesian3(position1.x - position0.x / 2, position1.y - position0.y / 2, position1.z - position0.z / 2);
    }

    public screenPositionToCartesian3(screenPos: Cartesian2) {
        const camera = this.cesiumService.getViewer().camera;

        return camera.pickEllipsoid(screenPos);
    }
}
